import { parameterUtils, type ModelData, type ModelParameter, type Position } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

/**
 * Template15 Layout:
 * [LeftFixed][SingleDoor][MiddleFixed][DoubleDoor1|Center|DoubleDoor2][RightFixed]
 *
 * Each door section has a transom glass panel above and a door glass panel below,
 * separated by horizontal frame profiles.
 */

const FRAME_THICKNESS = 60

const DIMENSION_LIMITS = {
  MIN_WIDTH: 3000,
  MAX_WIDTH: 5000,
  MIN_HEIGHT: 1800,
  MAX_HEIGHT: 4500,
  MIN_BOTTOM_LEFT_WIDTH: 200,
  MAX_BOTTOM_LEFT_WIDTH: 700,
  MIN_BOTTOM_MIDDLE_WIDTH: 300,
  MAX_BOTTOM_MIDDLE_WIDTH: 800,
  MIN_BOTTOM_RIGHT_WIDTH: 200,
  MAX_BOTTOM_RIGHT_WIDTH: 700,
}

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

// ─── Helper: Calculate section positions based on bottom frame widths ───

function calculateSectionPositions(params: ModelParameter[]) {
  const sections = {
    leftFixed: getParam(params, 'bottom_left_frame_profile')?.width || 300,
    singleDoor: getParam(params, 'single_door_bottom_frame_profile')?.width || 900,
    middleFixed: getParam(params, 'bottom_middle_frame_profile')?.width || 480,
    doubleDoor1: getParam(params, 'double_door_bottom_frame_profile_1')?.width || 780,
    doubleDoor2: getParam(params, 'double_door_bottom_frame_profile_2')?.width || 780,
    rightFixed: getParam(params, 'bottom_right_frame_profile')?.width || 300,
  }

  const middleVerticalWidth =
    getParam(params, 'double_door_middle_vertical_frame_profile_center')?.width ||
    FRAME_THICKNESS

  const positions = {
    leftFixed: 0,
    singleDoor: sections.leftFixed,
    middleFixed: sections.leftFixed + sections.singleDoor,
    doubleDoor1: sections.leftFixed + sections.singleDoor + sections.middleFixed,
    doubleDoor2:
      sections.leftFixed +
      sections.singleDoor +
      sections.middleFixed +
      sections.doubleDoor1 +
      middleVerticalWidth,
    rightFixed:
      sections.leftFixed +
      sections.singleDoor +
      sections.middleFixed +
      sections.doubleDoor1 +
      middleVerticalWidth +
      sections.doubleDoor2,
  }

  return { sections, positions, middleVerticalWidth }
}

// ─── Helper: Calculate transom heights ───

function calculateTransomHeights(params: ModelParameter[]) {
  const singleDoorTransomGlassHeight =
    getParam(params, 'single_door_top_glass_panel')?.height || 200
  const doubleDoorTransomGlassHeight =
    getParam(params, 'double_door_top_glass_panel')?.height || 200

  return {
    singleDoorTransomTotalHeight:
      FRAME_THICKNESS + singleDoorTransomGlassHeight + FRAME_THICKNESS,
    doubleDoorTransomTotalHeight:
      FRAME_THICKNESS + doubleDoorTransomGlassHeight + FRAME_THICKNESS,
    singleDoorTransomGlassHeight,
    doubleDoorTransomGlassHeight,
  }
}

// ─── Helper: Calculate door section heights ───

function calculateDoorSectionHeights(params: ModelParameter[], totalHeight: number) {
  const transomHeights = calculateTransomHeights(params)
  const bottomY = totalHeight - FRAME_THICKNESS

  return {
    singleDoorFrameHeight:
      bottomY - transomHeights.singleDoorTransomTotalHeight + FRAME_THICKNESS,
    doubleDoorFrameHeight:
      bottomY - transomHeights.doubleDoorTransomTotalHeight + FRAME_THICKNESS,
    singleDoorYStart: transomHeights.singleDoorTransomTotalHeight,
    doubleDoorYStart: transomHeights.doubleDoorTransomTotalHeight,
    bottomY,
  }
}

// ─── Helper: Validate dimensions ───

function validateDimensions(width: number, height: number) {
  return {
    width: Math.max(DIMENSION_LIMITS.MIN_WIDTH, Math.min(DIMENSION_LIMITS.MAX_WIDTH, width)),
    height: Math.max(DIMENSION_LIMITS.MIN_HEIGHT, Math.min(DIMENSION_LIMITS.MAX_HEIGHT, height)),
  }
}

// ─── Helper: Update hardware Y positions ───

function updateHardwarePositions(
  model: ModelData,
  panelId: string,
  hardwareType: string,
  newYPosition: number,
) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware?.[hardwareType]) return

  const updatedHardware = panel.hardware[hardwareType]!.map((item) => ({
    ...item,
    position: { ...item.position, y: newYPosition },
  }))

  updateParam(model, panelId, {
    hardware: { ...panel.hardware, [hardwareType]: updatedHardware },
  })
}

// ─── Helper: Update hinge positions proportionally ───

function updateHingePositions(
  model: ModelData,
  frameId: string,
  newFrameHeight: number,
) {
  const frame = getParam(model.parameters!, frameId)
  if (!frame?.hardware?.hinges) return

  const oldFrameHeight = frame.height || newFrameHeight
  const updatedHinges = frame.hardware.hinges.map((hinge) => {
    const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
    const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
    const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
    let newY: number
    if (isBottomHinge1) {
      newY = Math.round(newFrameHeight * 0.75)
    } else if (isBottomHinge2) {
      newY = Math.round(newFrameHeight * 0.85)
    } else {
      newY = Math.max(
        60,
        Math.min(
          newFrameHeight - (hinge.height || 100) - 60,
          relativePosition * newFrameHeight,
        ),
      )
    }
    return { ...hinge, position: { ...hinge.position, y: newY } }
  })

  updateParam(model, frameId, {
    hardware: { ...frame.hardware, hinges: updatedHinges },
  })
}

// ─── Helper: Recalculate all positions ───

function recalculatePositions(model: ModelData, recalculateHeights = false) {
  const totalHeight = model.height!
  const { sections, positions, middleVerticalWidth } = calculateSectionPositions(
    model.parameters!,
  )
  const transomHeights = calculateTransomHeights(model.parameters!)
  const doorHeights = calculateDoorSectionHeights(model.parameters!, totalHeight)

  const singleDoorYStart = transomHeights.singleDoorTransomTotalHeight
  const doubleDoorYStart = transomHeights.doubleDoorTransomTotalHeight
  const bottomY = totalHeight - FRAME_THICKNESS

  const currentVerticalFrameHeight = recalculateHeights
    ? totalHeight - FRAME_THICKNESS
    : getParam(model.parameters!, 'vertical_frame_profile_1')?.height ||
      totalHeight - FRAME_THICKNESS

  const currentSingleDoorFrameHeight = recalculateHeights
    ? doorHeights.singleDoorFrameHeight
    : getParam(model.parameters!, 'single_door_left_frame_profile')?.height ||
      doorHeights.singleDoorFrameHeight

  const currentDoubleDoorFrameHeight = recalculateHeights
    ? doorHeights.doubleDoorFrameHeight
    : getParam(model.parameters!, 'double_door_1_left_frame_profile')?.height ||
      doorHeights.doubleDoorFrameHeight

  const currentSingleDoorGlassHeight = recalculateHeights
    ? currentSingleDoorFrameHeight - FRAME_THICKNESS * 2
    : getParam(model.parameters!, 'single_door_glass_panel')?.height || 1760

  const currentDoubleDoorGlassHeight = recalculateHeights
    ? currentDoubleDoorFrameHeight - FRAME_THICKNESS * 2
    : getParam(model.parameters!, 'double_door_glass_panel_1')?.height || 1760

  // Update total width
  const newTotalWidth = positions.rightFixed + sections.rightFixed
  model.width = newTotalWidth
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })
  updateParam(model, 'right_frame_profile', {
    position: { x: newTotalWidth - FRAME_THICKNESS, y: 0, z: 0 },
  })

  const bottomFrameY = recalculateHeights
    ? bottomY
    : getParam(model.parameters!, 'bottom_middle_frame_profile')?.position?.y || bottomY

  // Bottom frame positions
  updateParam(model, 'bottom_left_frame_profile', {
    position: { x: 0, y: bottomFrameY, z: 0 },
  })
  updateParam(model, 'bottom_middle_frame_profile', {
    position: { x: positions.middleFixed, y: bottomFrameY, z: 0 },
  })
  updateParam(model, 'single_door_bottom_frame_profile', {
    position: { x: positions.singleDoor, y: bottomFrameY, z: 0 },
  })
  updateParam(model, 'double_door_bottom_frame_profile_1', {
    position: { x: positions.doubleDoor1, y: bottomFrameY, z: 0 },
  })
  updateParam(model, 'double_door_bottom_frame_profile_2', {
    position: { x: positions.doubleDoor2, y: bottomFrameY, z: 0 },
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: { x: positions.rightFixed, y: bottomFrameY, z: 0 },
  })

  // Vertical frame positions
  updateParam(model, 'vertical_frame_profile_1', {
    position: { x: positions.singleDoor - FRAME_THICKNESS, y: FRAME_THICKNESS, z: 0 },
    height: currentVerticalFrameHeight,
  })
  updateParam(model, 'vertical_frame_profile_2', {
    position: { x: positions.middleFixed, y: FRAME_THICKNESS, z: 0 },
    height: currentVerticalFrameHeight,
  })
  updateParam(model, 'vertical_frame_profile_3', {
    position: { x: positions.doubleDoor1 - FRAME_THICKNESS, y: FRAME_THICKNESS, z: 0 },
    height: currentVerticalFrameHeight,
  })
  updateParam(model, 'vertical_frame_profile_4', {
    position: { x: positions.rightFixed, y: FRAME_THICKNESS, z: 0 },
    height: currentVerticalFrameHeight,
  })

  // Single door section
  updateParam(model, 'single_door_left_frame_profile', {
    position: { x: positions.singleDoor, y: singleDoorYStart, z: 0 },
    height: currentSingleDoorFrameHeight,
  })
  updateParam(model, 'single_door_right_frame_profile', {
    position: {
      x: positions.singleDoor + sections.singleDoor - FRAME_THICKNESS,
      y: singleDoorYStart,
      z: 0,
    },
    height: currentSingleDoorFrameHeight,
  })
  updateParam(model, 'single_door_top_frame_profile', {
    position: { x: positions.singleDoor, y: singleDoorYStart, z: 0 },
    width: sections.singleDoor,
  })
  updateParam(model, 'single_door_middle_horizontal_frame_profile', {
    position: {
      x: positions.singleDoor,
      y: FRAME_THICKNESS + transomHeights.singleDoorTransomGlassHeight,
      z: 0,
    },
    width: sections.singleDoor,
  })
  updateParam(model, 'single_door_glass_panel', {
    position: {
      x: positions.singleDoor + FRAME_THICKNESS,
      y: singleDoorYStart + FRAME_THICKNESS,
      z: 0,
    },
    width: sections.singleDoor - FRAME_THICKNESS * 2,
    height: currentSingleDoorGlassHeight,
  })

  // Double door section
  updateParam(model, 'double_door_1_left_frame_profile', {
    position: { x: positions.doubleDoor1, y: doubleDoorYStart, z: 0 },
    height: currentDoubleDoorFrameHeight,
  })
  updateParam(model, 'double_door_1_right_frame_profile', {
    position: {
      x: positions.doubleDoor1 + sections.doubleDoor1 - FRAME_THICKNESS,
      y: doubleDoorYStart,
      z: 0,
    },
    height: currentDoubleDoorFrameHeight,
  })
  updateParam(model, 'double_door_middle_vertical_frame_profile_center', {
    position: {
      x: positions.doubleDoor1 + sections.doubleDoor1,
      y: doubleDoorYStart,
      z: 0,
    },
    height: currentDoubleDoorFrameHeight,
  })
  updateParam(model, 'double_door_2_left_frame_profile', {
    position: { x: positions.doubleDoor2, y: doubleDoorYStart, z: 0 },
    height: currentDoubleDoorFrameHeight,
  })
  updateParam(model, 'double_door_2_right_frame_profile', {
    position: {
      x: positions.doubleDoor2 + sections.doubleDoor2 - FRAME_THICKNESS,
      y: doubleDoorYStart,
      z: 0,
    },
    height: currentDoubleDoorFrameHeight,
  })
  updateParam(model, 'double_door_1_top_frame_profile', {
    position: { x: positions.doubleDoor1, y: doubleDoorYStart, z: 0 },
    width: sections.doubleDoor1,
  })
  updateParam(model, 'double_door_2_top_frame_profile', {
    position: { x: positions.doubleDoor2, y: doubleDoorYStart, z: 0 },
    width: sections.doubleDoor2,
  })
  updateParam(model, 'double_door_middle_horizontal_frame_profile', {
    position: {
      x: positions.doubleDoor1,
      y: FRAME_THICKNESS + transomHeights.doubleDoorTransomGlassHeight,
      z: 0,
    },
    width: sections.doubleDoor1 + sections.doubleDoor2 + middleVerticalWidth,
  })
  updateParam(model, 'double_door_glass_panel_1', {
    position: {
      x: positions.doubleDoor1 + FRAME_THICKNESS,
      y: doubleDoorYStart + FRAME_THICKNESS,
      z: 0,
    },
    width: sections.doubleDoor1 - FRAME_THICKNESS * 2,
    height: currentDoubleDoorGlassHeight,
  })
  updateParam(model, 'double_door_glass_panel_2', {
    position: {
      x: positions.doubleDoor2 + FRAME_THICKNESS,
      y: doubleDoorYStart + FRAME_THICKNESS,
      z: 0,
    },
    width: sections.doubleDoor2 - FRAME_THICKNESS * 2,
    height: currentDoubleDoorGlassHeight,
  })

  // Fixed glass panels
  const fixedPanelHeight = recalculateHeights
    ? totalHeight - FRAME_THICKNESS * 2
    : getParam(model.parameters!, 'left_glass_panel')?.height ||
      totalHeight - FRAME_THICKNESS * 2

  updateParam(model, 'left_glass_panel', {
    position: { x: FRAME_THICKNESS, y: FRAME_THICKNESS, z: 0 },
    width: sections.leftFixed - FRAME_THICKNESS * 2,
    height: fixedPanelHeight,
  })
  updateParam(model, 'middle_glass_panel', {
    position: { x: positions.middleFixed + FRAME_THICKNESS, y: FRAME_THICKNESS, z: 0 },
    width: sections.middleFixed - FRAME_THICKNESS * 2,
    height: fixedPanelHeight,
  })
  updateParam(model, 'right_glass_panel', {
    position: { x: positions.rightFixed + FRAME_THICKNESS, y: FRAME_THICKNESS, z: 0 },
    width: sections.rightFixed - FRAME_THICKNESS * 2,
    height: fixedPanelHeight,
  })

  // Transom glass panels
  updateParam(model, 'single_door_top_glass_panel', {
    position: { x: positions.singleDoor, y: FRAME_THICKNESS, z: 0 },
    width: sections.singleDoor,
    height: transomHeights.singleDoorTransomGlassHeight,
  })
  updateParam(model, 'double_door_top_glass_panel', {
    position: { x: positions.doubleDoor1, y: FRAME_THICKNESS, z: 0 },
    width: sections.doubleDoor1 + sections.doubleDoor2 + middleVerticalWidth,
    height: transomHeights.doubleDoorTransomGlassHeight,
  })
}

// ─── Helper: Shift components to the right ───

function shiftComponentsRight(model: ModelData, componentIds: string[], widthDifference: number) {
  componentIds.forEach((id) => {
    const component = getParam(model.parameters!, id)
    if (component?.position) {
      updateParam(model, id, {
        position: { ...component.position, x: (component.position.x || 0) + widthDifference },
      })
    }
  })
}

// ─── Helper: Get components for a section ───

function getSectionComponents(sectionName: string): string[] {
  const componentMap: Record<string, string[]> = {
    leftFixed: ['left_glass_panel', 'vertical_frame_profile_1'],
    singleDoor: [
      'single_door_left_frame_profile',
      'single_door_right_frame_profile',
      'single_door_top_frame_profile',
      'single_door_bottom_frame_profile',
      'single_door_top_glass_panel',
      'single_door_glass_panel',
      'single_door_middle_horizontal_frame_profile',
    ],
    middleFixed: ['middle_glass_panel', 'vertical_frame_profile_2', 'vertical_frame_profile_3'],
    doubleDoor1: [
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_1_top_frame_profile',
      'double_door_bottom_frame_profile_1',
      'double_door_glass_panel_1',
    ],
    doubleDoor2: [
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
      'double_door_2_top_frame_profile',
      'double_door_bottom_frame_profile_2',
      'double_door_glass_panel_2',
    ],
    rightFixed: ['right_glass_panel', 'vertical_frame_profile_4'],
  }
  return componentMap[sectionName] || []
}

// ─── Dimension handlers ───

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(numValue, model.height!)
  const finalWidth = validated.width

  model.width = finalWidth
  updateParam(model, 'top_frame_profile', { width: finalWidth })
  updateParam(model, 'right_frame_profile', {
    position: { x: finalWidth - FRAME_THICKNESS, y: 0, z: 0 },
  })

  if (cascadeUpdates) {
    const fixedDoorWidths = {
      singleDoor:
        getParam(model.parameters!, 'single_door_bottom_frame_profile')?.width || 900,
      doubleDoor1:
        getParam(model.parameters!, 'double_door_bottom_frame_profile_1')?.width || 780,
      doubleDoor2:
        getParam(model.parameters!, 'double_door_bottom_frame_profile_2')?.width || 780,
    }

    const centerVerticalFrameWidth = FRAME_THICKNESS
    const totalDoorWidth =
      fixedDoorWidths.singleDoor +
      fixedDoorWidths.doubleDoor1 +
      fixedDoorWidths.doubleDoor2 +
      centerVerticalFrameWidth

    const remainingWidthForFixedPanels = finalWidth - totalDoorWidth

    const currentFixedPanels = {
      left: getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 300,
      middle: getParam(model.parameters!, 'bottom_middle_frame_profile')?.width || 480,
      right: getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 220,
    }

    const currentFixedTotal =
      currentFixedPanels.left + currentFixedPanels.middle + currentFixedPanels.right
    const widthDifference = remainingWidthForFixedPanels - currentFixedTotal
    const equalShare = widthDifference / 3

    const newFixedPanels = {
      left: Math.max(
        DIMENSION_LIMITS.MIN_BOTTOM_LEFT_WIDTH,
        Math.min(DIMENSION_LIMITS.MAX_BOTTOM_LEFT_WIDTH, Math.round(currentFixedPanels.left + equalShare)),
      ),
      middle: Math.max(
        DIMENSION_LIMITS.MIN_BOTTOM_MIDDLE_WIDTH,
        Math.min(DIMENSION_LIMITS.MAX_BOTTOM_MIDDLE_WIDTH, Math.round(currentFixedPanels.middle + equalShare)),
      ),
      right: Math.max(
        DIMENSION_LIMITS.MIN_BOTTOM_RIGHT_WIDTH,
        Math.min(DIMENSION_LIMITS.MAX_BOTTOM_RIGHT_WIDTH, Math.round(currentFixedPanels.right + equalShare)),
      ),
    }

    // Adjust middle panel for rounding difference
    const calculatedFixedPanelsTotal =
      newFixedPanels.left + newFixedPanels.middle + newFixedPanels.right
    const difference = remainingWidthForFixedPanels - calculatedFixedPanelsTotal
    newFixedPanels.middle += difference

    updateParam(model, 'bottom_left_frame_profile', { width: newFixedPanels.left })
    updateParam(model, 'bottom_middle_frame_profile', { width: newFixedPanels.middle })
    updateParam(model, 'bottom_right_frame_profile', { width: newFixedPanels.right })

    recalculatePositions(model)
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(model.width!, numValue)
  const finalHeight = validated.height

  model.height = finalHeight
  updateParam(model, 'left_frame_profile', { height: finalHeight })
  updateParam(model, 'right_frame_profile', { height: finalHeight })

  if (cascadeUpdates) {
    recalculatePositions(model, true)

    const doorHeights = calculateDoorSectionHeights(model.parameters!, finalHeight)
    const singleDoorFrameHeight = doorHeights.singleDoorFrameHeight
    const doubleDoorFrameHeight = doorHeights.doubleDoorFrameHeight

    // Update handle positions: doorFrameHeight * 53%
    updateHardwarePositions(
      model,
      'single_door_glass_panel',
      'handles',
      Math.round(singleDoorFrameHeight * 0.53),
    )
    updateHardwarePositions(
      model,
      'double_door_glass_panel_1',
      'handles',
      Math.round(doubleDoorFrameHeight * 0.53),
    )
    updateHardwarePositions(
      model,
      'double_door_glass_panel_2',
      'handles',
      Math.round(doubleDoorFrameHeight * 0.53),
    )
  }

  return model
}

const bottomLeftFrameWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedWidth = Math.max(
    DIMENSION_LIMITS.MIN_BOTTOM_LEFT_WIDTH,
    Math.min(DIMENSION_LIMITS.MAX_BOTTOM_LEFT_WIDTH, numValue),
  )
  const oldWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 300
  const widthDifference = clampedWidth - oldWidth

  updateParam(model, 'bottom_left_frame_profile', { width: clampedWidth })
  updateParam(model, 'left_glass_panel', {
    width: clampedWidth - FRAME_THICKNESS * 2,
  })

  if (cascadeUpdates) {
    const newTotalWidth = model.width! + widthDifference
    const validated = validateDimensions(newTotalWidth, model.height!)
    const finalWidth = validated.width

    model.width = finalWidth
    updateParam(model, 'top_frame_profile', { width: finalWidth })
    updateParam(model, 'right_frame_profile', {
      position: { x: finalWidth - FRAME_THICKNESS, y: 0, z: 0 },
    })

    // Shift all components to the right of left fixed section
    const componentsToShift = [
      ...getSectionComponents('singleDoor'),
      ...getSectionComponents('middleFixed'),
      ...getSectionComponents('doubleDoor1'),
      ...getSectionComponents('doubleDoor2'),
      ...getSectionComponents('rightFixed'),
      'double_door_top_glass_panel',
      'double_door_middle_horizontal_frame_profile',
    ]
    shiftComponentsRight(model, componentsToShift, widthDifference)

    // Fix vertical frame positions
    const { sections, positions } = calculateSectionPositions(model.parameters!)

    const vf2 = getParam(model.parameters!, 'vertical_frame_profile_2')
    if (vf2) {
      updateParam(model, 'vertical_frame_profile_2', {
        position: { x: positions.middleFixed, y: vf2.position?.y || FRAME_THICKNESS, z: vf2.position?.z || 0 },
      })
    }
    const vf3 = getParam(model.parameters!, 'vertical_frame_profile_3')
    if (vf3) {
      updateParam(model, 'vertical_frame_profile_3', {
        position: { x: positions.doubleDoor1 - FRAME_THICKNESS, y: vf3.position?.y || FRAME_THICKNESS, z: vf3.position?.z || 0 },
      })
    }
    const vf4 = getParam(model.parameters!, 'vertical_frame_profile_4')
    if (vf4) {
      updateParam(model, 'vertical_frame_profile_4', {
        position: { x: positions.rightFixed, y: vf4.position?.y || FRAME_THICKNESS, z: vf4.position?.z || 0 },
      })
    }
    const ddmv = getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')
    if (ddmv) {
      updateParam(model, 'double_door_middle_vertical_frame_profile_center', {
        position: { x: positions.doubleDoor1 + sections.doubleDoor1, y: ddmv.position?.y || 0, z: ddmv.position?.z || 0 },
      })
    }
  }

  return model
}

const handleHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId } = options as { positionId?: string }
  if (!positionId) return model

  const panel = getParam(model.parameters!, positionId as string)
  if (panel?.hardware?.handles && panel.hardware.handles.length > 0) {
    const updatedHandles = panel.hardware.handles.map((handle) => ({
      ...handle,
      position: { ...handle.position, y: numValue },
    }))
    updateParam(model, positionId as string, {
      hardware: { ...panel.hardware, handles: updatedHandles },
    })
  }

  return model
}

const singleDoorBottomFrameWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedWidth = Math.max(600, Math.min(1500, numValue))

  updateParam(model, 'single_door_bottom_frame_profile', { width: clampedWidth })
  updateParam(model, 'single_door_top_glass_panel', { width: clampedWidth })
  updateParam(model, 'single_door_glass_panel', {
    width: clampedWidth - FRAME_THICKNESS * 2,
  })
  updateParam(model, 'single_door_middle_horizontal_frame_profile', { width: clampedWidth })
  updateParam(model, 'single_door_top_frame_profile', { width: clampedWidth })

  if (cascadeUpdates) {
    const oldWidth =
      getParam(model.parameters!, 'single_door_bottom_frame_profile')?.width || clampedWidth
    // Since we already updated, we need the pre-update value to calc difference
    // But we already set it, so we recalculate from the stored initial
    const singleDoorLeft = getParam(model.parameters!, 'single_door_left_frame_profile')
    if (singleDoorLeft) {
      updateParam(model, 'single_door_right_frame_profile', {
        position: {
          ...(getParam(model.parameters!, 'single_door_right_frame_profile')?.position || {
            x: 0,
            y: 0,
            z: 0,
          }),
          x: (singleDoorLeft.position?.x || 0) + clampedWidth - FRAME_THICKNESS,
        },
      })
    }

    recalculatePositions(model)
  }

  return model
}

const bottomMiddleFrameWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedWidth = Math.max(300, Math.min(800, numValue))
  const oldWidth = getParam(model.parameters!, 'bottom_middle_frame_profile')?.width || 480
  const widthDifference = clampedWidth - oldWidth

  updateParam(model, 'bottom_middle_frame_profile', { width: clampedWidth })
  updateParam(model, 'middle_glass_panel', {
    width: clampedWidth - FRAME_THICKNESS * 2,
  })

  if (cascadeUpdates) {
    const newTotalWidth = model.width! + widthDifference
    const validated = validateDimensions(newTotalWidth, model.height!)
    const finalWidth = validated.width

    model.width = finalWidth
    updateParam(model, 'top_frame_profile', { width: finalWidth })

    // Shift double door and right sections
    const doubleDoorComponents = [
      'double_door_bottom_frame_profile_1',
      'double_door_bottom_frame_profile_2',
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_middle_vertical_frame_profile_center',
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
      'double_door_top_glass_panel',
      'double_door_glass_panel_1',
      'double_door_glass_panel_2',
      'double_door_middle_horizontal_frame_profile',
      'double_door_1_top_frame_profile',
      'double_door_2_top_frame_profile',
      'vertical_frame_profile_3',
      'vertical_frame_profile_4',
      'bottom_right_frame_profile',
      'right_glass_panel',
    ]
    shiftComponentsRight(model, doubleDoorComponents, widthDifference)

    updateParam(model, 'right_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'right_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        x: finalWidth - FRAME_THICKNESS,
      },
    })
  }

  return model
}

const doubleDoorBottomFrame1Width: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const middleVerticalWidth =
    getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')?.width || 60
  const actualDoorWidth = numValue - middleVerticalWidth / 2
  const clampedWidth = Math.max(600, Math.min(1500, actualDoorWidth))

  if (cascadeUpdates) {
    const oldWidth =
      getParam(model.parameters!, 'double_door_bottom_frame_profile_1')?.width || 900
    const widthDifference = clampedWidth - oldWidth

    updateParam(model, 'double_door_bottom_frame_profile_1', { width: clampedWidth })
    updateParam(model, 'double_door_glass_panel_1', {
      width: clampedWidth - FRAME_THICKNESS * 2,
    })
    updateParam(model, 'double_door_1_top_frame_profile', { width: clampedWidth })

    // Update right frame position
    const dd1Left = getParam(model.parameters!, 'double_door_1_left_frame_profile')
    if (dd1Left) {
      updateParam(model, 'double_door_1_right_frame_profile', {
        position: {
          ...(getParam(model.parameters!, 'double_door_1_right_frame_profile')?.position || {
            x: 0,
            y: 0,
            z: 0,
          }),
          x: (dd1Left.position?.x || 0) + clampedWidth - FRAME_THICKNESS,
        },
      })
    }

    // Shift middle vertical and door 2 components
    const middleVertical = getParam(
      model.parameters!,
      'double_door_middle_vertical_frame_profile_center',
    )
    if (middleVertical) {
      updateParam(model, 'double_door_middle_vertical_frame_profile_center', {
        position: {
          ...(middleVertical.position || { x: 0, y: 0, z: 0 }),
          x: (middleVertical.position?.x || 0) + widthDifference,
        },
      })
    }

    const door2Components = [
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
      'double_door_glass_panel_2',
      'double_door_2_top_frame_profile',
      'double_door_bottom_frame_profile_2',
    ]
    shiftComponentsRight(model, door2Components, widthDifference)

    // Update transom width
    const doubleDoor2Width =
      getParam(model.parameters!, 'double_door_bottom_frame_profile_2')?.width || 900
    const totalDoubleDoorWidth = clampedWidth + doubleDoor2Width + FRAME_THICKNESS
    updateParam(model, 'double_door_top_glass_panel', { width: totalDoubleDoorWidth })
    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      width: totalDoubleDoorWidth,
    })

    const newTotalWidth = model.width! + widthDifference
    const validated = validateDimensions(newTotalWidth, model.height!)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })

    // Shift right section
    shiftComponentsRight(
      model,
      ['vertical_frame_profile_4', 'bottom_right_frame_profile', 'right_glass_panel'],
      widthDifference,
    )
    updateParam(model, 'right_frame_profile', {
      position: {
        ...getParam(model.parameters!, 'right_frame_profile')?.position,
        x: validated.width - FRAME_THICKNESS,
      } as Position,
    })
  } else {
    updateParam(model, 'double_door_bottom_frame_profile_1', { width: clampedWidth })
    updateParam(model, 'double_door_glass_panel_1', {
      width: clampedWidth - FRAME_THICKNESS * 2,
    })
    updateParam(model, 'double_door_1_top_frame_profile', { width: clampedWidth })
  }

  return model
}

const doubleDoorBottomFrame2Width: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const middleVerticalWidth =
    getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')?.width || 60
  const actualDoorWidth = numValue - middleVerticalWidth / 2
  const clampedWidth = Math.max(600, Math.min(1500, actualDoorWidth))

  if (cascadeUpdates) {
    const oldWidth =
      getParam(model.parameters!, 'double_door_bottom_frame_profile_2')?.width || 900
    const widthDifference = clampedWidth - oldWidth

    updateParam(model, 'double_door_bottom_frame_profile_2', { width: clampedWidth })
    updateParam(model, 'double_door_glass_panel_2', {
      width: clampedWidth - FRAME_THICKNESS * 2,
    })
    updateParam(model, 'double_door_2_top_frame_profile', { width: clampedWidth })

    const dd2Left = getParam(model.parameters!, 'double_door_2_left_frame_profile')
    if (dd2Left) {
      updateParam(model, 'double_door_2_right_frame_profile', {
        position: {
          ...(getParam(model.parameters!, 'double_door_2_right_frame_profile')?.position || {
            x: 0,
            y: 0,
            z: 0,
          }),
          x: (dd2Left.position?.x || 0) + clampedWidth - FRAME_THICKNESS,
        },
      })
    }

    // Update transom width
    const doubleDoor1Width =
      getParam(model.parameters!, 'double_door_bottom_frame_profile_1')?.width || 900
    const totalDoubleDoorWidth = doubleDoor1Width + clampedWidth + FRAME_THICKNESS
    updateParam(model, 'double_door_top_glass_panel', { width: totalDoubleDoorWidth })
    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      width: totalDoubleDoorWidth,
    })

    const newTotalWidth = model.width! + widthDifference
    const validated = validateDimensions(newTotalWidth, model.height!)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })

    shiftComponentsRight(
      model,
      ['vertical_frame_profile_4', 'bottom_right_frame_profile', 'right_glass_panel'],
      widthDifference,
    )
    updateParam(model, 'right_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'right_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        x: validated.width - FRAME_THICKNESS,
      },
    })
  } else {
    updateParam(model, 'double_door_bottom_frame_profile_2', { width: clampedWidth })
    updateParam(model, 'double_door_glass_panel_2', {
      width: clampedWidth - FRAME_THICKNESS * 2,
    })
    updateParam(model, 'double_door_2_top_frame_profile', { width: clampedWidth })
  }

  return model
}

const bottomRightFrameWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedWidth = Math.max(200, Math.min(700, numValue))
  const oldWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 220
  const widthDifference = clampedWidth - oldWidth

  updateParam(model, 'bottom_right_frame_profile', { width: clampedWidth })
  updateParam(model, 'right_glass_panel', {
    width: clampedWidth - FRAME_THICKNESS * 2,
  })

  if (cascadeUpdates) {
    const newTotalWidth = model.width! + widthDifference
    const validated = validateDimensions(newTotalWidth, model.height!)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })
    updateParam(model, 'right_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'right_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        x: validated.width - FRAME_THICKNESS,
      },
    })
  }

  return model
}

const singleDoorTransomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const totalFrameHeight = FRAME_THICKNESS * 2
  const actualTransomHeight = numValue - totalFrameHeight
  const clampedTransomHeight = Math.max(150, Math.min(600, actualTransomHeight))

  if (cascadeUpdates) {
    const oldTransomHeight =
      getParam(model.parameters!, 'single_door_top_glass_panel')?.height || 200
    const transomHeightDifference = clampedTransomHeight - oldTransomHeight

    updateParam(model, 'single_door_top_glass_panel', { height: clampedTransomHeight })

    const currentDoorHeight =
      getParam(model.parameters!, 'single_door_glass_panel')?.height || 1760
    const newDoorHeight = currentDoorHeight - transomHeightDifference
    const clampedDoorHeight = Math.max(800, Math.min(2000, newDoorHeight))

    const finalHeight = model.height!
    const transomFrameY = FRAME_THICKNESS + clampedTransomHeight
    const doorTopFrameY = transomFrameY + FRAME_THICKNESS
    const doorGlassPanelY = doorTopFrameY + FRAME_THICKNESS
    const doorFrameHeight = finalHeight - doorTopFrameY

    // Update horizontal frame, top frame, vertical frames
    updateParam(model, 'single_door_middle_horizontal_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'single_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        y: transomFrameY,
      },
    })
    updateParam(model, 'single_door_top_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'single_door_top_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        y: doorTopFrameY,
      },
    })
    updateParam(model, 'single_door_left_frame_profile', {
      height: doorFrameHeight,
      position: {
        ...(getParam(model.parameters!, 'single_door_left_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        y: doorTopFrameY,
      },
    })
    updateParam(model, 'single_door_right_frame_profile', {
      height: doorFrameHeight,
      position: {
        ...(getParam(model.parameters!, 'single_door_right_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        y: doorTopFrameY,
      },
    })

    // Update handle positions
    updateHardwarePositions(model, 'single_door_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))

    // Update door glass panel
    updateParam(model, 'single_door_glass_panel', {
      height: clampedDoorHeight,
      position: {
        ...(getParam(model.parameters!, 'single_door_glass_panel')?.position || { x: 0, y: 0, z: 0 }),
        y: doorGlassPanelY,
      },
    })
    updateHardwarePositions(model, 'single_door_glass_panel', 'handles', Math.round(doorFrameHeight * 0.53))

    // Update bottom frames, fixed glass panels, vertical frames, double door section
    const bottomY = finalHeight - FRAME_THICKNESS
    const bottomFrames = [
      'bottom_left_frame_profile',
      'single_door_bottom_frame_profile',
      'bottom_middle_frame_profile',
      'double_door_bottom_frame_profile_1',
      'double_door_bottom_frame_profile_2',
      'bottom_right_frame_profile',
    ]
    bottomFrames.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) {
        updateParam(model, id, { position: { ...(f.position || { x: 0, y: 0, z: 0 }), y: bottomY } })
      }
    })

    // Fixed glass panels height
    const fixedHeight = finalHeight - FRAME_THICKNESS * 2
    updateParam(model, 'left_glass_panel', { height: fixedHeight })
    updateParam(model, 'middle_glass_panel', { height: fixedHeight })
    updateParam(model, 'right_glass_panel', { height: fixedHeight })

    // Vertical frame heights
    const vfHeight = finalHeight - FRAME_THICKNESS
    ;['vertical_frame_profile_1', 'vertical_frame_profile_2', 'vertical_frame_profile_3', 'vertical_frame_profile_4'].forEach((id) => {
      updateParam(model, id, { height: vfHeight })
    })

    // Update double door section to match
    const ddTransomGlassHeight =
      getParam(model.parameters!, 'double_door_top_glass_panel')?.height || 200
    const ddTransomTotalHeight = FRAME_THICKNESS + ddTransomGlassHeight + FRAME_THICKNESS
    const ddTopFrameY = ddTransomTotalHeight
    const ddFrameHeight = finalHeight - ddTopFrameY
    const ddGlassHeight = Math.max(800, Math.min(2000, ddFrameHeight - FRAME_THICKNESS * 2))

    const ddFrameIds = [
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_middle_vertical_frame_profile_center',
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
    ]
    ddFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) {
        updateParam(model, id, { height: ddFrameHeight, position: { ...(f.position || {}), y: ddTopFrameY } })
      }
    })
    ;['double_door_1_top_frame_profile', 'double_door_2_top_frame_profile'].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) {
        updateParam(model, id, { position: { ...(f.position || {}), y: ddTopFrameY } as Position })
      }
    })
    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')?.position || {}),
        y: FRAME_THICKNESS + ddTransomGlassHeight,
      } as Position,
    })
    updateParam(model, 'double_door_glass_panel_1', {
      height: ddGlassHeight,
      position: {
        ...(getParam(model.parameters!, 'double_door_glass_panel_1')?.position || {}),
        y: ddTopFrameY + FRAME_THICKNESS,
      } as Position,
    })
    updateParam(model, 'double_door_glass_panel_2', {
      height: ddGlassHeight,
      position: {
        ...(getParam(model.parameters!, 'double_door_glass_panel_2')?.position || {}),
        y: ddTopFrameY + FRAME_THICKNESS,
      } as Position,
    })

    // Update double door handle and hinge positions
    updateHardwarePositions(model, 'double_door_glass_panel_2', 'handles', Math.round(ddFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_left_frame_profile', 'handles', Math.round(ddFrameHeight * 0.53))
  } else {
    updateParam(model, 'single_door_top_glass_panel', { height: clampedTransomHeight })
  }

  return model
}

const doubleDoorTransomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const totalFrameHeight = FRAME_THICKNESS * 2
  const actualTransomHeight = numValue - totalFrameHeight
  const clampedHeight = Math.max(150, Math.min(600, actualTransomHeight))

  if (cascadeUpdates) {
    const oldTransomHeight =
      getParam(model.parameters!, 'double_door_top_glass_panel')?.height || 200
    const transomHeightDifference = clampedHeight - oldTransomHeight

    const { sections, positions, middleVerticalWidth } = calculateSectionPositions(model.parameters!)

    updateParam(model, 'double_door_top_glass_panel', {
      height: clampedHeight,
      position: { x: positions.doubleDoor1, y: FRAME_THICKNESS, z: 0 },
      width: sections.doubleDoor1 + sections.doubleDoor2 + middleVerticalWidth,
    })

    const currentDoorHeight =
      getParam(model.parameters!, 'double_door_glass_panel_1')?.height || 1760
    const newDoorHeight = currentDoorHeight - transomHeightDifference
    const clampedDoorHeight = Math.max(800, Math.min(2000, newDoorHeight))

    updateParam(model, 'double_door_glass_panel_1', { height: clampedDoorHeight })
    updateParam(model, 'double_door_glass_panel_2', { height: clampedDoorHeight })

    const finalHeight = model.height!
    const transomFrameY = FRAME_THICKNESS + clampedHeight
    const doorTopFrameY = transomFrameY + FRAME_THICKNESS
    const doorGlassPanelY = doorTopFrameY + FRAME_THICKNESS
    const doorFrameHeight = finalHeight - doorTopFrameY

    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      position: {
        ...(getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }),
        y: transomFrameY,
      },
    })
    ;['double_door_1_top_frame_profile', 'double_door_2_top_frame_profile'].forEach((id) => {
      updateParam(model, id, {
        position: {
          ...(getParam(model.parameters!, id)?.position || { x: 0, y: 0, z: 0 }),
          y: doorTopFrameY,
        },
      })
    })

    const ddFrameIds = [
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_middle_vertical_frame_profile_center',
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
    ]
    ddFrameIds.forEach((id) => {
      updateParam(model, id, {
        height: doorFrameHeight,
        position: {
          ...(getParam(model.parameters!, id)?.position || { x: 0, y: 0, z: 0 }),
          y: doorTopFrameY,
        },
      })
    })

    // Handle positions
    updateHardwarePositions(model, 'double_door_1_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_right_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))

    updateParam(model, 'double_door_glass_panel_1', {
      height: clampedDoorHeight,
      position: {
        ...(getParam(model.parameters!, 'double_door_glass_panel_1')?.position || { x: 0, y: 0, z: 0 }),
        y: doorGlassPanelY,
      },
    })
    updateParam(model, 'double_door_glass_panel_2', {
      height: clampedDoorHeight,
      position: {
        ...(getParam(model.parameters!, 'double_door_glass_panel_2')?.position || { x: 0, y: 0, z: 0 }),
        y: doorGlassPanelY,
      },
    })

    updateHardwarePositions(model, 'double_door_glass_panel_1', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_glass_panel_2', 'handles', Math.round(doorFrameHeight * 0.53))

    // Update bottom, fixed, and vertical frames + single door section
    const bottomY = finalHeight - FRAME_THICKNESS
    const bottomFrames = [
      'bottom_left_frame_profile',
      'single_door_bottom_frame_profile',
      'bottom_middle_frame_profile',
      'double_door_bottom_frame_profile_1',
      'double_door_bottom_frame_profile_2',
      'bottom_right_frame_profile',
    ]
    bottomFrames.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) {
        updateParam(model, id, { position: { ...(f.position || { x: 0, y: 0, z: 0 }), y: bottomY } })
      }
    })

    const fixedHeight = finalHeight - FRAME_THICKNESS * 2
    updateParam(model, 'left_glass_panel', { height: fixedHeight })
    updateParam(model, 'middle_glass_panel', { height: fixedHeight })
    updateParam(model, 'right_glass_panel', { height: fixedHeight })

    const vfHeight = finalHeight - FRAME_THICKNESS
    ;['vertical_frame_profile_1', 'vertical_frame_profile_2', 'vertical_frame_profile_3', 'vertical_frame_profile_4'].forEach((id) => {
      updateParam(model, id, { height: vfHeight })
    })

    // Single door section
    const sdTransomGlassHeight =
      getParam(model.parameters!, 'single_door_top_glass_panel')?.height || 200
    const sdTransomTotalHeight = FRAME_THICKNESS + sdTransomGlassHeight + FRAME_THICKNESS
    const sdTopFrameY = sdTransomTotalHeight
    const sdFrameHeight = finalHeight - sdTopFrameY
    const sdGlassHeight = Math.max(800, Math.min(2000, sdFrameHeight - FRAME_THICKNESS * 2))

    updateParam(model, 'single_door_left_frame_profile', {
      height: sdFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_left_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_right_frame_profile', {
      height: sdFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_right_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_top_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_top_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_middle_horizontal_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: FRAME_THICKNESS + sdTransomGlassHeight },
    })
    updateParam(model, 'single_door_glass_panel', {
      height: sdGlassHeight,
      position: { ...(getParam(model.parameters!, 'single_door_glass_panel')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY + FRAME_THICKNESS },
    })
    updateHardwarePositions(model, 'single_door_glass_panel', 'handles', Math.round(sdFrameHeight * 0.53))
  } else {
    updateParam(model, 'double_door_top_glass_panel', { height: clampedHeight })
  }

  return model
}

const singleDoorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const doorFrameHeight = Math.max(800, Math.min(3000, numValue))

  const oldDoorFrameHeight =
    getParam(model.parameters!, 'single_door_left_frame_profile')?.height || doorFrameHeight
  const heightDifference = doorFrameHeight - oldDoorFrameHeight
  const oldGlassPanelHeight =
    getParam(model.parameters!, 'single_door_glass_panel')?.height ||
    doorFrameHeight - FRAME_THICKNESS * 2
  const newGlassPanelHeight = oldGlassPanelHeight + heightDifference
  const clampedHeight = Math.max(800, Math.min(2000, newGlassPanelHeight))

  if (cascadeUpdates) {
    const transomGlassHeight =
      getParam(model.parameters!, 'single_door_top_glass_panel')?.height || 200
    const transomTotalHeight = FRAME_THICKNESS + transomGlassHeight + FRAME_THICKNESS
    const newTotalHeight = transomTotalHeight + doorFrameHeight
    const validated = validateDimensions(model.width!, newTotalHeight)
    const finalHeight = validated.height

    model.height = finalHeight
    updateParam(model, 'left_frame_profile', { height: finalHeight })
    updateParam(model, 'right_frame_profile', { height: finalHeight })

    updateParam(model, 'single_door_glass_panel', { height: clampedHeight })

    const doorTopFrameY = transomTotalHeight
    const doorGlassPanelY = doorTopFrameY + FRAME_THICKNESS

    updateParam(model, 'single_door_middle_horizontal_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: FRAME_THICKNESS + transomGlassHeight },
    })
    updateParam(model, 'single_door_top_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_top_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: doorTopFrameY },
    })
    updateParam(model, 'single_door_left_frame_profile', {
      height: doorFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_left_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: doorTopFrameY },
    })
    updateParam(model, 'single_door_right_frame_profile', {
      height: doorFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_right_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: doorTopFrameY },
    })

    updateHardwarePositions(model, 'single_door_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))

    updateParam(model, 'single_door_glass_panel', {
      height: doorFrameHeight - FRAME_THICKNESS * 2,
      position: { ...(getParam(model.parameters!, 'single_door_glass_panel')?.position || { x: 0, y: 0, z: 0 }), y: doorGlassPanelY },
    })
    updateHardwarePositions(model, 'single_door_glass_panel', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHingePositions(model, 'single_door_right_frame_profile', doorFrameHeight)

    const bottomY = finalHeight - FRAME_THICKNESS
    updateParam(model, 'single_door_bottom_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_bottom_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: bottomY },
    })

    // Vertical frames & fixed panels
    const vfHeight = finalHeight - FRAME_THICKNESS
    ;['vertical_frame_profile_1', 'vertical_frame_profile_2', 'vertical_frame_profile_3', 'vertical_frame_profile_4'].forEach((id) => {
      updateParam(model, id, { height: vfHeight })
    })
    const fixedHeight = finalHeight - FRAME_THICKNESS * 2
    ;['left_glass_panel', 'middle_glass_panel', 'right_glass_panel'].forEach((id) => {
      updateParam(model, id, { height: fixedHeight })
    })

    // Double door section
    const ddTransomGlassHeight = getParam(model.parameters!, 'double_door_top_glass_panel')?.height || 200
    const ddTransomTotalHeight = FRAME_THICKNESS + ddTransomGlassHeight + FRAME_THICKNESS
    const ddTopFrameY = ddTransomTotalHeight
    const ddFrameHeight = finalHeight - ddTopFrameY

    const ddFrameIds = [
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_middle_vertical_frame_profile_center',
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
    ]
    ddFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) {
        updateParam(model, id, { height: ddFrameHeight, position: { ...(f.position || {}), y: ddTopFrameY } as Position })
      }
    })
    ;['double_door_1_top_frame_profile', 'double_door_2_top_frame_profile'].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...(f.position || {}), y: ddTopFrameY } as Position })
    })
    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      position: { ...(getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')?.position || {}), y: FRAME_THICKNESS + ddTransomGlassHeight } as Position,
    })
    updateParam(model, 'double_door_glass_panel_1', {
      height: ddFrameHeight - FRAME_THICKNESS * 2,
      position: { ...(getParam(model.parameters!, 'double_door_glass_panel_1')?.position || {}), y: ddTopFrameY + FRAME_THICKNESS } as Position,
    })
    updateParam(model, 'double_door_glass_panel_2', {
      height: ddFrameHeight - FRAME_THICKNESS * 2,
      position: { ...(getParam(model.parameters!, 'double_door_glass_panel_2')?.position || {}), y: ddTopFrameY + FRAME_THICKNESS } as Position,
    })

    updateHardwarePositions(model, 'double_door_glass_panel_1', 'handles', Math.round(ddFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_glass_panel_2', 'handles', Math.round(ddFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_left_frame_profile', 'handles', Math.round(ddFrameHeight * 0.53))

    updateHingePositions(model, 'double_door_1_left_frame_profile', ddFrameHeight)
    updateHingePositions(model, 'double_door_2_left_frame_profile', ddFrameHeight)
    updateHingePositions(model, 'double_door_1_right_frame_profile', ddFrameHeight)
    updateHingePositions(model, 'double_door_2_right_frame_profile', ddFrameHeight)

    // Bottom frames
    ;['bottom_left_frame_profile', 'bottom_middle_frame_profile', 'bottom_right_frame_profile', 'double_door_bottom_frame_profile_1', 'double_door_bottom_frame_profile_2'].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...(f.position || {}), y: bottomY } })
    })
  } else {
    updateParam(model, 'single_door_left_frame_profile', { height: doorFrameHeight })
    updateParam(model, 'single_door_glass_panel', { height: clampedHeight })
  }

  return model
}

const doubleDoorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const doorFrameHeight = Math.max(800, Math.min(3000, numValue))

  const oldDoorFrameHeight =
    getParam(model.parameters!, 'double_door_middle_vertical_frame_profile_center')?.height ||
    doorFrameHeight
  const heightDifference = doorFrameHeight - oldDoorFrameHeight

  const oldGlassPanel1Height =
    getParam(model.parameters!, 'double_door_glass_panel_1')?.height ||
    doorFrameHeight - FRAME_THICKNESS * 2
  const oldGlassPanel2Height =
    getParam(model.parameters!, 'double_door_glass_panel_2')?.height ||
    doorFrameHeight - FRAME_THICKNESS * 2

  const newGlassPanel1Height = oldGlassPanel1Height + heightDifference
  const newGlassPanel2Height = oldGlassPanel2Height + heightDifference
  const clampedHeight1 = Math.max(800, Math.min(2000, newGlassPanel1Height))
  const clampedHeight2 = Math.max(800, Math.min(2000, newGlassPanel2Height))

  if (cascadeUpdates) {
    const transomGlassHeight =
      getParam(model.parameters!, 'double_door_top_glass_panel')?.height || 200
    const transomTotalHeight = FRAME_THICKNESS + transomGlassHeight + FRAME_THICKNESS
    const newTotalHeight = transomTotalHeight + doorFrameHeight
    const validated = validateDimensions(model.width!, newTotalHeight)
    const finalHeight = validated.height

    model.height = finalHeight
    updateParam(model, 'left_frame_profile', { height: finalHeight })
    updateParam(model, 'right_frame_profile', { height: finalHeight })

    updateParam(model, 'double_door_glass_panel_1', { height: clampedHeight1 })
    updateParam(model, 'double_door_glass_panel_2', { height: clampedHeight2 })

    const doorTopFrameY = transomTotalHeight
    const doorGlassPanelY = doorTopFrameY + FRAME_THICKNESS

    updateParam(model, 'double_door_middle_horizontal_frame_profile', {
      position: { ...(getParam(model.parameters!, 'double_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: FRAME_THICKNESS + transomGlassHeight },
    })
    ;['double_door_1_top_frame_profile', 'double_door_2_top_frame_profile'].forEach((id) => {
      updateParam(model, id, { position: { ...(getParam(model.parameters!, id)?.position || { x: 0, y: 0, z: 0 }), y: doorTopFrameY } })
    })

    const ddFrameIds = [
      'double_door_1_left_frame_profile',
      'double_door_1_right_frame_profile',
      'double_door_middle_vertical_frame_profile_center',
      'double_door_2_left_frame_profile',
      'double_door_2_right_frame_profile',
    ]
    ddFrameIds.forEach((id) => {
      updateParam(model, id, {
        height: doorFrameHeight,
        position: { ...(getParam(model.parameters!, id)?.position || { x: 0, y: 0, z: 0 }), y: doorTopFrameY },
      })
    })

    updateHardwarePositions(model, 'double_door_1_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_right_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_2_left_frame_profile', 'handles', Math.round(doorFrameHeight * 0.53))

    updateParam(model, 'double_door_glass_panel_1', {
      height: doorFrameHeight - FRAME_THICKNESS * 2,
      position: { ...(getParam(model.parameters!, 'double_door_glass_panel_1')?.position || { x: 0, y: 0, z: 0 }), y: doorGlassPanelY },
    })
    updateParam(model, 'double_door_glass_panel_2', {
      height: doorFrameHeight - FRAME_THICKNESS * 2,
      position: { ...(getParam(model.parameters!, 'double_door_glass_panel_2')?.position || { x: 0, y: 0, z: 0 }), y: doorGlassPanelY },
    })

    updateHardwarePositions(model, 'double_door_glass_panel_1', 'handles', Math.round(doorFrameHeight * 0.53))
    updateHardwarePositions(model, 'double_door_glass_panel_2', 'handles', Math.round(doorFrameHeight * 0.53))

    updateHingePositions(model, 'double_door_1_left_frame_profile', doorFrameHeight)
    updateHingePositions(model, 'double_door_1_right_frame_profile', doorFrameHeight)
    updateHingePositions(model, 'double_door_2_left_frame_profile', doorFrameHeight)
    updateHingePositions(model, 'double_door_2_right_frame_profile', doorFrameHeight)

    // Single door + bottom frames + vertical frames + fixed panels
    const bottomY = finalHeight - FRAME_THICKNESS
    const vfHeight = finalHeight - FRAME_THICKNESS
    const fixedHeight = finalHeight - FRAME_THICKNESS * 2

    ;['vertical_frame_profile_1', 'vertical_frame_profile_2', 'vertical_frame_profile_3', 'vertical_frame_profile_4'].forEach((id) => {
      updateParam(model, id, { height: vfHeight })
    })
    ;['left_glass_panel', 'middle_glass_panel', 'right_glass_panel'].forEach((id) => {
      updateParam(model, id, { height: fixedHeight })
    })

    // Single door section
    const sdTransomGlassHeight = getParam(model.parameters!, 'single_door_top_glass_panel')?.height || 200
    const sdTransomTotalHeight = FRAME_THICKNESS + sdTransomGlassHeight + FRAME_THICKNESS
    const sdTopFrameY = sdTransomTotalHeight
    const sdFrameHeight = finalHeight - sdTopFrameY
    const sdGlassHeight = Math.max(800, Math.min(2000, sdFrameHeight - FRAME_THICKNESS * 2))

    updateParam(model, 'single_door_left_frame_profile', {
      height: sdFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_left_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_right_frame_profile', {
      height: sdFrameHeight,
      position: { ...(getParam(model.parameters!, 'single_door_right_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_top_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_top_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY },
    })
    updateParam(model, 'single_door_middle_horizontal_frame_profile', {
      position: { ...(getParam(model.parameters!, 'single_door_middle_horizontal_frame_profile')?.position || { x: 0, y: 0, z: 0 }), y: FRAME_THICKNESS + sdTransomGlassHeight },
    })
    updateParam(model, 'single_door_glass_panel', {
      height: sdGlassHeight,
      position: { ...(getParam(model.parameters!, 'single_door_glass_panel')?.position || { x: 0, y: 0, z: 0 }), y: sdTopFrameY + FRAME_THICKNESS },
    })
    updateHardwarePositions(model, 'single_door_glass_panel', 'handles', Math.round(sdFrameHeight * 0.53))
    updateHardwarePositions(model, 'single_door_left_frame_profile', 'handles', Math.round(sdFrameHeight * 0.53))
    updateHingePositions(model, 'single_door_right_frame_profile', sdFrameHeight)

    ;['bottom_left_frame_profile', 'single_door_bottom_frame_profile', 'bottom_middle_frame_profile', 'bottom_right_frame_profile', 'double_door_bottom_frame_profile_1', 'double_door_bottom_frame_profile_2'].forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...(f.position || {}), y: bottomY } })
    })
  } else {
    updateParam(model, 'double_door_middle_vertical_frame_profile_center', {
      height: doorFrameHeight,
    })
    updateParam(model, 'double_door_glass_panel_1', { height: clampedHeight1 })
    updateParam(model, 'double_door_glass_panel_2', { height: clampedHeight2 })
  }

  return model
}

export const model15DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  bottomLeftFrameWidth,
  handleHeight,
  singleDoorBottomFrameWidth,
  bottomMiddleFrameWidth,
  doubleDoorBottomFrame1Width,
  doubleDoorBottomFrame2Width,
  bottomRightFrameWidth,
  singleDoorTransomHeight,
  doubleDoorTransomHeight,
  singleDoorPanelHeight,
  doubleDoorPanelHeight,
}
